// src/authClient.js
// One axios client for ALL panels (admin/teacher/student/parent)
// - Adds Authorization + X-Edumonics-School-Id on every request
// - Proactively refreshes access tokens before expiry
// - Retries requests once on 401 by refreshing in the background
// - Works with either access_token or accessToken keys (compat)
// - Exposes helpers: persistAuth, clearAuth, setSchoolId, getRole, hasValidAccessToken
// - Exposes <AuthBootstrap> to initialize timers/interceptors at app start

import axios from "axios";
import React, { useEffect, useRef } from "react";
import { API_CONFIG } from "./config/api";

// ---------- Config ----------
const API_BASE_URL = API_CONFIG.BASE_URL;


// Storage keys (supports both snake_case & camelCase)
const K = {
    access_snake: "access_token",
    access_camel: "accessToken",
    refresh_snake: "refresh_token",
    refresh_camel: "refreshToken",
    role: "student", // "admin" | "teacher" | "student" | "parent"
    school: "school_id",
    refreshEndpoint: "refresh_endpoint", // optional override, e.g. "/admin/token/refresh"
};

// ---------- Storage helpers ----------
function getFirst(...keys) {
    for (const k of keys) {
        const v = localStorage.getItem(k);
        if (v) return v;
    }
    return null;
}

function setAll(value, ...keys) {
    keys.forEach((k) => localStorage.setItem(k, value));
}

function removeAll(...keys) {
    keys.forEach((k) => localStorage.removeItem(k));
}

export function getAccessToken() {
    return getFirst(K.access_snake, K.access_camel);
}

export function getRefreshToken() {
    return getFirst(K.refresh_snake, K.refresh_camel);
}

export function getSchoolId() {
    return localStorage.getItem(K.school);
}

export function setSchoolId(id) {
    if (id) localStorage.setItem(K.school, id);
}

export function getRole() {
    // 1) explicit role
    const explicit = localStorage.getItem(K.role);
    if (explicit) return explicit;
    // 2) infer from JWT claim
    const t = getAccessToken();
    const role = t ? (decodeJwtSafe(t)?.role || decodeJwtSafe(t)?.claims?.role) : null;
    return role || "student";
}

export function persistAuth({ accessToken, refreshToken, role, schoolId, refreshEndpoint } = {}) {
    if (accessToken) setAll(accessToken, K.access_snake, K.access_camel);
    if (refreshToken) setAll(refreshToken, K.refresh_snake, K.refresh_camel);
    if (role) localStorage.setItem(K.role, role);
    if (schoolId) localStorage.setItem(K.school, schoolId);
    if (refreshEndpoint) localStorage.setItem(K.refreshEndpoint, refreshEndpoint);
    scheduleProactiveRefresh(); // start/refresh timer
}

export function clearAuth() {
    removeAll(K.access_snake, K.access_camel, K.refresh_snake, K.refresh_camel, K.role, K.refreshEndpoint);
}

export function hasValidAccessToken(skewSeconds = 10) {
    const t = getAccessToken();
    if (!t) return false;
    const payload = decodeJwtSafe(t);
    if (!payload?.exp) return true; // no exp => treat as valid (or your call)
    const now = Math.floor(Date.now() / 1000);
    return payload.exp - now > skewSeconds;
}

// ---------- JWT decode (no lib) ----------
function base64UrlDecode(str) {
    try {
        const pad = "=".repeat((4 - (str.length % 4)) % 4);
        const base64 = (str + pad).replace(/-/g, "+").replace(/_/g, "/");
        const text = atob(base64);
        try {
            return decodeURIComponent(escape(text)); // handles utf8
        } catch {
            return text;
        }
    } catch {
        return null;
    }
}

function decodeJwtSafe(token) {
    try {
        const [, payload] = token.split(".");
        return JSON.parse(base64UrlDecode(payload) || "{}");
    } catch {
        return null;
    }
}

// ---------- Axios instance ----------
export const api = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: false, // adjust if you use cookies
});

// Request: add Authorization + Tenant
api.interceptors.request.use((config) => {
    // Do not add Authorization to preflight-like calls — but axios never makes OPTIONS manually
    const access = getAccessToken();
    if (access) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${access}`;
    }
    const schoolId = getSchoolId();
    if (schoolId) {
        config.headers = config.headers || {};
        config.headers["X-Edumonics-School-Id"] = schoolId;
    }
    // Optional: role header (can help your middleware if needed)
    const role = getRole();
    if (role) {
        config.headers["X-Edumonics-Role"] = role;
    }
    return config;
});

// Response: on 401 try one refresh, then retry original request
let refreshPromise = null;
api.interceptors.response.use(
    (resp) => resp,
    async (error) => {
        const { config, response } = error || {};
        const status = response?.status;
        const isUnauthorized = status === 401 || status === 419;
        const isLoginOrRefresh =
            config?.url?.includes("/login") || config?.url?.includes("/token/refresh") || config?._isRefreshCall;

        if (!isUnauthorized || isLoginOrRefresh) {
            return Promise.reject(error);
        }

        // guard: avoid infinite loop
        if (config._retry) {
            // hard logout
            clearAuth();
            return Promise.reject(error);
        }
        config._retry = true;

        try {
            if (!refreshPromise) refreshPromise = doRefreshToken(); // one global refresh in flight
            const newAccess = await refreshPromise;
            refreshPromise = null;

            // Update header & retry
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${newAccess}`;
            return api(config);
        } catch (e) {
            refreshPromise = null;
            clearAuth();
            // Optionally route to login:
            if (typeof window !== "undefined") window.location.assign("/");
            return Promise.reject(e);
        }
    }
);

// ---------- Refresh logic ----------
function resolveRefreshEndpoint(role) {
    // 1) explicit override if you set it (works for any panel)
    const override = localStorage.getItem(K.refreshEndpoint);
    if (override) return override;

    // 2) common patterns per role (adjust if your backend differs)
    // Your student panel already has POST /token/refresh?refresh_token=...
    if (role === "student") return "/token/refresh";
    if (role === "admin") return "/admin/token/refresh";
    if (role === "teacher") return "/teacher/token/refresh";
    if (role === "parent") return "/parent/token/refresh";

    // 3) final fallback
    return "/token/refresh";
}

async function doRefreshToken() {
    const refresh = getRefreshToken();
    if (!refresh) throw new Error("No refresh token");

    const role = getRole();
    const endpoint = resolveRefreshEndpoint(role);

    // Use a bare client so we don't attach an expired Authorization header
    const bare = axios.create({ baseURL: API_BASE_URL });

    // Your FastAPI student route signature is:
    //   POST /token/refresh  (query param: refresh_token)
    // So first try as a query param. If that fails, try JSON body.
    let resp;
    try {
        resp = await bare.post(endpoint, null, {
            params: { refresh_token: refresh },
            headers: { "X-Edumonics-School-Id": getSchoolId() || "" },
        });
    } catch {
        resp = await bare.post(
            endpoint,
            { refresh_token: refresh },
            { headers: { "Content-Type": "application/json", "X-Edumonics-School-Id": getSchoolId() || "" } }
        );
    }

    const { accessToken, refreshToken } = extractTokens(resp?.data);
    if (!accessToken) throw new Error("Refresh failed: no access token returned");
    // Persist any updated tokens
    persistAuth({
        accessToken,
        refreshToken: refreshToken || refresh, // keep old if backend returns only access
        role,
    });
    return accessToken;
}

// Try common response shapes
function extractTokens(data = {}) {
    return {
        accessToken:
            data.access_token ||
            data.accessToken ||
            data.token ||
            (data.data && (data.data.access_token || data.data.accessToken)),
        refreshToken:
            data.refresh_token ||
            data.refreshToken ||
            (data.data && (data.data.refresh_token || data.data.refreshToken)),
    };
}

// ---------- Proactive refresh (timer) ----------
let refreshTimerId = null;

function clearRefreshTimer() {
    if (refreshTimerId) {
        clearTimeout(refreshTimerId);
        refreshTimerId = null;
    }
}

function scheduleProactiveRefresh() {
    clearRefreshTimer();
    const t = getAccessToken();
    if (!t) return;

    const payload = decodeJwtSafe(t);
    // default: refresh 60s before expiry; if no exp, refresh every 12 minutes
    if (!payload?.exp) {
        refreshTimerId = setTimeout(safeRefresh, 12 * 60 * 1000);
        return;
    }
    const nowMs = Date.now();
    const expMs = payload.exp * 1000;
    const leadMs = 60 * 1000; // refresh 1 min before expiry
    const delay = Math.max(2_000, expMs - nowMs - leadMs); // min 2s
    refreshTimerId = setTimeout(safeRefresh, delay);
}

async function safeRefresh() {
    try {
        if (!hasValidAccessToken(5)) {
            await doRefreshToken();
        }
        scheduleProactiveRefresh(); // reschedule only on success
    } catch {
        // Refresh failed — stop the loop; interceptor handles 401s on next real request
        clearRefreshTimer();
    }
}

// ---------- React bootstrap wrapper ----------
export function AuthBootstrap({ children }) {
    // useRef so we init exactly once
    const inited = useRef(false);

    useEffect(() => {
        if (inited.current) return;
        inited.current = true;

        // If we already have a token, schedule a proactive refresh
        if (getAccessToken()) {
            scheduleProactiveRefresh();
        }
    }, []);

    return <>{children}</>;
}
